// Slug minting + normalization. One shape for every generated handle:
//
//   <name-derived>-<nano>      where <nano> is [a-z0-9]{4}
//
// The name-derived prefix is regenerated from the current name on
// rename (lowercase ASCII, runs of non-alphanumerics collapsed to
// dashes, edges trimmed). The 4-char suffix is assigned once at mint
// time and never changes — it carries identity through renames and
// gives ~1.7M variants per shared name.
//
// Slugs are *display IDs*. They live in exports, filenames, and URL
// fragments. Foreign references bind to the prefixed opaque ids
// (`stu_…`, `pcpt_…`, …), never to the slug.

import { customAlphabet } from 'nanoid';

const NANO_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const NANO_LENGTH = 4;
const nano = customAlphabet(NANO_ALPHABET, NANO_LENGTH);

/** Regex form of the nano suffix, for callers that need to recognize one. */
export const SLUG_NANO_PATTERN = /-[a-z0-9]{4}$/;

export interface SlugifyOptions {
  /** Returned when the input has no alphanumerics. */
  fallback?: string;
  /** Truncate the kebab portion at a word boundary at-or-before this length. */
  maxLen?: number;
}

/**
 * Lowercase, ASCII-fold, dash-collapse, trim. Pure normalization —
 * does not append a nano suffix. Use this on the user-edit path so
 * typing a label doesn't keep regenerating identity.
 */
export function slugifyName(label: string, opts: SlugifyOptions = {}): string {
  const { fallback = 'item', maxLen = 32 } = opts;
  const raw = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!raw) return fallback;
  if (raw.length <= maxLen) return raw;
  const window = raw.slice(0, maxLen);
  const boundary = window.lastIndexOf('-');
  return boundary > 0 ? window.slice(0, boundary) : window;
}

export interface MintSlugOptions extends SlugifyOptions {
  /**
   * Slugs already in use in the relevant namespace. The mint loop
   * re-rolls the nano suffix on collision. With 1.7M variants and
   * realistic library sizes the loop almost always exits on the first
   * try, but the guard is here for paranoia.
   */
  taken?: Iterable<string>;
}

/**
 * Mint a fresh slug: `<slugifyName(label)>-<nano4>`.
 *
 * The nano suffix is the stable identity component. Callers that want
 * to *re-derive* the prefix on rename should keep the suffix sticky
 * and only rebuild the part before the final `-xxxx`.
 */
export function mintSlug(label: string, opts: MintSlugOptions = {}): string {
  const base = slugifyName(label, opts);
  const taken = opts.taken ? new Set(opts.taken) : null;
  // Realistic library sizes never approach the 1.7M ceiling; 16 tries
  // is plenty of headroom. If we somehow exhaust it, fall through and
  // let the last roll win — duplicates are recoverable, hangs are not.
  for (let i = 0; i < 16; i += 1) {
    const candidate = `${base}-${nano()}`;
    if (!taken || !taken.has(candidate)) return candidate;
  }
  return `${base}-${nano()}`;
}

/**
 * Split a slug into its name-derived prefix and nano suffix. Returns
 * `null` for the suffix when the slug doesn't carry one (legacy slugs
 * minted before the nano convention).
 */
export function splitSlug(slug: string): { prefix: string; nano: string | null } {
  const match = SLUG_NANO_PATTERN.exec(slug);
  if (!match) return { prefix: slug, nano: null };
  return { prefix: slug.slice(0, match.index), nano: match[0].slice(1) };
}

/**
 * Rebuild the prefix from the current name while keeping the existing
 * nano suffix. Used on rename: identity stays, the human-readable part
 * tracks the new name.
 */
export function rederiveSlug(
  slug: string,
  newName: string,
  opts: SlugifyOptions = {},
): string {
  const { nano: existing } = splitSlug(slug);
  const prefix = slugifyName(newName, opts);
  if (existing) return `${prefix}-${existing}`;
  // Legacy slug without a nano — mint a fresh suffix so the result
  // matches the canonical shape going forward.
  return mintSlug(newName, opts);
}
